import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Phone, ArrowRight } from 'lucide-react';
import { QUICK_COUNTRY_CODES } from './constants';

export default function GuestForm({
  guestName, setGuestName, guestCountryCode, setGuestCountryCode, guestPhone, setGuestPhone,
  guestOTP, setGuestOTP, otpRequested, setOtpRequested, loading, onRequestOTP, onSubmit,
}) {
  return (
    <TabsContent value="guest">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="guest-form">
        <div>
          <Label htmlFor="guest-name" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            Your Name
          </Label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="guest-name"
              data-testid="guest-name-input"
              type="text"
              placeholder="Enter your name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
              required
              disabled={otpRequested}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="guest-phone" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
            Phone Number
          </Label>
          <div className="flex gap-2">
            <Select
              value={guestCountryCode}
              onValueChange={setGuestCountryCode}
              disabled={otpRequested}
            >
              <SelectTrigger
                data-testid="guest-country-code-select"
                className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary w-[140px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {QUICK_COUNTRY_CODES.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    <span className="flex items-center gap-2">
                      <span>{country.flag}</span>
                      <span>{country.code}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="guest-phone"
                data-testid="guest-phone-input"
                type="tel"
                placeholder="Enter phone number"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value.replace(/\D/g, ''))}
                className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary pl-12"
                required
                disabled={otpRequested}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Enter phone number without country code
          </p>
        </div>
        {!otpRequested ? (
          <Button
            type="button"
            data-testid="request-otp-button"
            onClick={onRequestOTP}
            className="w-full btn-primary h-14"
            disabled={loading}
          >
            {loading ? 'Sending OTP...' : 'Request OTP'}
          </Button>
        ) : (
          <>
            <div>
              <Label htmlFor="guest-otp" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
                OTP Code
              </Label>
              <Input
                id="guest-otp"
                data-testid="guest-otp-input"
                type="text"
                placeholder="12345"
                value={guestOTP}
                onChange={(e) => setGuestOTP(e.target.value)}
                className="h-14 rounded-2xl bg-secondary border-transparent focus:border-primary"
                maxLength={5}
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="guest-submit-button"
              className="w-full btn-volt h-14"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify & Continue'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              data-testid="change-phone-button"
              onClick={() => {
                setOtpRequested(false);
                setGuestOTP('');
              }}
              className="w-full"
            >
              Change Details
            </Button>
          </>
        )}
      </form>
    </TabsContent>
  );
}
